import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  DateRangePickerComponent,
  DialogComponent,
  IntegratorSelectComponent,
  StatusFilter,
  TrackerPositionFilters,
  WhColumnDirective,
  WhSortEvent,
  WhTableComponent,
  WhTableEmptyDirective,
  NEUTRAL_PILL_CLASS,
  channelPalette,
} from '@wheere-front/shared-ui';
import { Gateway, GatewayService } from '@wheere/api-site';
import { Position, PositionService, TrackerPositionsResponse } from '@wheere/api-tracker';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { MapPositionsReadHistoryComponent } from './map-position/map-positions-read-history.component';

type SortField = 'timestamp' | 'altitude';
type SortDir = 'asc' | 'desc';
@Component({
  selector: 'lib-position-history-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DateRangePickerComponent,
    DatePipe,
    DialogComponent,
    IntegratorSelectComponent,
    MapPositionsReadHistoryComponent,
    TranslocoModule,
    WhTableComponent,
    WhColumnDirective,
    WhTableEmptyDirective,
  ],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <div
      class="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 sm:gap-3 sm:p-3 dark:border-slate-700 dark:bg-slate-800">
      <lib-date-range-picker
        class="w-full sm:w-auto"
        [initialFilters]="currentFilters"
        (filtersApplied)="onFiltersApplied($event)" />

      <div class="flex flex-1 items-center justify-end gap-2">
        <label class="flex items-center gap-1.5">
          <span class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">{{
            'positionHistory.toolbar.max' | transloco
          }}</span>
          <input
            type="number"
            [(ngModel)]="maxPositions"
            min="100"
            max="100000"
            step="500"
            class="focus:border-primary-500 h-8 w-24 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 outline-none transition-colors dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100" />
        </label>

        <button
          type="button"
          class="bg-primary-500 hover:bg-primary-600 inline-flex h-8 items-center gap-1 rounded-md px-3 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 disabled:dark:bg-slate-600"
          [disabled]="!filters?.from || total() === 0 || mapLoading()"
          [title]="'positionHistory.toolbar.showOnMapTitle' | transloco: { max: maxPositions }"
          (click)="openMap()">
          @if (mapLoading()) {
            <svg class="size-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" d="M4 12a8 8 0 018-8" />
            </svg>
          } @else {
            <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.553 2.776A1 1 0 0022 18.882V8.118a1 1 0 00-.553-.894L15 4m0 13V4m-6 3l6-3" />
            </svg>
          }
          {{ 'positionHistory.toolbar.showOnMap' | transloco }}
        </button>

        <button
          type="button"
          class="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          [disabled]="!filters?.from || total() === 0"
          [title]="'positionHistory.toolbar.csvTitle' | transloco"
          (click)="downloadCsv()">
          <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
          </svg>
          {{ 'positionHistory.toolbar.csv' | transloco }}
        </button>
      </div>
    </div>

    <!-- ── Status / counter strip ──────────────────────────────────────────
        Tells the operator what state the panel is in: how many fixes total,
        which page they're on, whether something's loading. Cheap to scan. -->
    <div
      class="mt-3 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-slate-900/40 dark:text-slate-400">
      <span>
        @if (loading()) {
          {{ 'positionHistory.status.loading' | transloco }}
        } @else if (!filters?.from) {
          {{ 'positionHistory.status.pickRange' | transloco }}
        } @else {
          <span
            [innerHTML]="
              'positionHistory.status.counter'
                | transloco
                  : {
                      total: total(),
                      page: currentPage() + 1,
                      totalPages: totalPages() || 1,
                    }
            "></span>
        }
      </span>
      <lib-integrator-select
        width="9rem"
        size="sm"
        accent="primary"
        [options]="pageSizeOptions()"
        [(ngModel)]="itemsPerPage"
        (ngModelChange)="onItemsPerPageChange()" />
    </div>

    <div class="mt-3">
      <wh-table
        [rows]="positions() ?? []"
        [loading]="loading()"
        [gridTemplate]="'minmax(220px,1.3fr) 120px 120px 110px 90px 90px 90px 100px'"
        [minWidth]="'940px'"
        [sortKey]="sortBy()"
        [sortDir]="sortDir()"
        (sort)="onSort($event)">
        <ng-template
          whColumn
          [header]="'positionHistory.columns.timestamp' | transloco"
          key="timestamp"
          [sortable]="true"
          let-p>
          <div class="whitespace-nowrap text-xs tabular-nums text-gray-900 dark:text-slate-100">
            {{ p.timestamp | date: 'dd/MM/yyyy HH:mm:ss' : 'UTC' }} UTC
          </div>
        </ng-template>
        <ng-template whColumn [header]="'positionHistory.columns.latitude' | transloco" let-p>
          <div class="font-mono">{{ formatNum(p.latitude, 6) }}</div>
        </ng-template>
        <ng-template whColumn [header]="'positionHistory.columns.longitude' | transloco" let-p>
          <div class="font-mono">{{ formatNum(p.longitude, 6) }}</div>
        </ng-template>
        <ng-template
          whColumn
          [header]="'positionHistory.columns.altitude' | transloco"
          key="altitude"
          [sortable]="true"
          let-p>
          <div class="font-mono">
            {{ p.altitude !== null && p.altitude !== undefined ? formatNum(p.altitude, 1) + ' m' : '—' }}
          </div>
        </ng-template>
        <ng-template whColumn [header]="'positionHistory.columns.source' | transloco" let-p>
          @if (p.source) {
            <span class="inline-block rounded px-1.5 py-0.5 text-xs font-bold" [class]="sourceClass(p.source)">
              {{ p.source }}
            </span>
          } @else {
            <span class="text-gray-400">—</span>
          }
        </ng-template>
        <ng-template whColumn [header]="'positionHistory.columns.online' | transloco" let-p>
          <span class="inline-block rounded px-1.5 py-0.5 text-xs font-bold" [class]="onlinePillClass(p.isOnline)">
            {{ (p.isOnline ? 'positionHistory.pills.on' : 'positionHistory.pills.off') | transloco }}
          </span>
        </ng-template>
        <ng-template whColumn [header]="'positionHistory.columns.trilateration' | transloco" let-p>
          @if (p.trilateration !== null && p.trilateration !== undefined) {
            <span
              class="inline-block rounded px-1.5 py-0.5 text-xs font-bold"
              [class]="trilaterationClass(p.trilateration)">
              {{ formatPct(p.trilateration) }}
            </span>
          } @else {
            <span class="text-gray-400">—</span>
          }
        </ng-template>
        <ng-template whTableEmpty>
          @if (!filters?.from) {
            {{ 'positionHistory.empty.pickRangeLoad' | transloco }}
          } @else {
            {{ 'positionHistory.empty.noPositions' | transloco }}
          }
        </ng-template>
      </wh-table>
    </div>

    <!-- ── Pagination ──────────────────────────────────────────────────── -->
    @if (totalPages() > 1) {
      <div class="mt-3 flex items-center justify-between gap-2">
        <button
          type="button"
          class="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          [disabled]="currentPage() === 0"
          (click)="goPage(currentPage() - 1)">
          {{ 'positionHistory.pagination.prev' | transloco }}
        </button>
        <span class="text-sm text-gray-500 dark:text-slate-400"> {{ currentPage() + 1 }} / {{ totalPages() }} </span>
        <button
          type="button"
          class="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
          [disabled]="currentPage() >= totalPages() - 1"
          (click)="goPage(currentPage() + 1)">
          {{ 'positionHistory.pagination.next' | transloco }}
        </button>
      </div>
    }

    <!-- ── Replay-on-map dialog (unchanged) ────────────────────────────── -->
    <lib-dialog #mapDialog>
      @if (mapDialog?.isOpen()) {
        <lib-map-read-positions
          [dialog]="mapDialog"
          [trackerId]="trackerId()"
          [positions]="mapPositions()"
          [gateways]="mapGateways()"
          [siteId]="siteId()" />
      }
    </lib-dialog>
  `,
})
export class PositionHistoryTableComponent {
  trackerId = input.required<string>();
  deviceId = input<string>('');
  /** Optional — when present, the replay map fetches site gateways so the rig topology
   * shows alongside the positions timeline. */
  siteId = input<string | undefined>(undefined);
  positionsLoaded = output<{ positions: Position[]; total: number }>();

  @ViewChild('mapDialog') mapDialog: DialogComponent | undefined;

  // ── Replay map state
  protected readonly mapPositions = signal<Position[]>([]);
  protected readonly mapGateways = signal<Gateway[]>([]);
  protected readonly mapLoading = signal(false);

  // ── Filter
  protected currentFilters: TrackerPositionFilters = {
    from: new Date(Date.now() - 60 * 60 * 1000),
    to: new Date(),
    statusFilter: StatusFilter.BOTH,
  };
  protected filters: TrackerPositionFilters | null = this.currentFilters;
  protected maxPositions = 3000;

  // ── Pagination + sort (signals so the template reacts cleanly)
  protected readonly currentPage = signal(0);
  protected itemsPerPage = signal(20);
  protected readonly pageSizes = [20, 50, 100];

  /** Sortable fields — extra ones (lat/lon) are intentionally left out, they
   * make no operational sense to sort by. */
  protected readonly sortBy = signal<SortField>('timestamp');
  protected readonly sortDir = signal<SortDir>('desc');

  // ── Data state
  protected readonly positions = signal<Position[] | null>(null);
  protected readonly total = signal(0);
  protected readonly loading = signal(false);

  protected readonly totalPages = computed(() => {
    const t = this.total();
    const per = this.itemsPerPage();
    if (!t || !per) return 0;
    return Math.ceil(t / per);
  });

  #positionService = inject(PositionService);
  #messageService = inject(MessageService);
  #siteGatewayService = inject(GatewayService);
  #transloco = inject(TranslocoService);

  /** Re-derive page-size labels whenever the active language changes so the
   * "/ page" suffix stays localised. */
  private readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });
  /** {@link IntegratorSelectComponent} requires {label, value} option shape;
   * derive once from pageSizes so we don't ship a string label per option. */
  protected readonly pageSizeOptions = computed(() => {
    this.activeLang(); // dependency — force re-eval on language switch
    return this.pageSizes.map(n => ({
      label: this.#transloco.translate('positionHistory.toolbar.perPage', { n }),
      value: n,
    }));
  });

  /**
   * Reset to page 0 and reload whenever the trackerId changes (so navigating
   * between two trackers in the same browser tab doesn't show the previous
   * tracker's positions until the operator re-applies the filter).
   */
  constructor() {
    effect(
      () => {
        const id = this.trackerId();
        if (!id) return;
        this.currentPage.set(0);
        this.fetchPositions();
      },
      { allowSignalWrites: true }
    );
  }

  protected onFiltersApplied(filters: TrackerPositionFilters): void {
    this.currentFilters = filters;
    this.filters = filters;
    this.currentPage.set(0);
    this.fetchPositions();
  }

  protected onItemsPerPageChange(): void {
    this.currentPage.set(0);
    this.fetchPositions();
  }

  protected goPage(p: number): void {
    if (p < 0 || p >= this.totalPages()) return;
    this.currentPage.set(p);
    this.fetchPositions();
  }

  protected onSort(e: WhSortEvent): void {
    this.sortBy.set(e.key as SortField);
    this.sortDir.set(e.dir);
    this.currentPage.set(0);
    this.fetchPositions();
  }

  // ── Data fetch
  private fetchPositions(): void {
    if (!this.filters?.from) return;
    this.loading.set(true);
    const sort = `${this.sortBy()},${this.sortDir()}`;
    this.#positionService
      .getPositionsByDateRange(
        this.trackerId(),
        this.filters.from,
        this.filters.to ?? undefined,
        this.filters.statusFilter,
        this.filters.sourceFilter as string[] | undefined,
        this.currentPage(),
        this.itemsPerPage(),
        sort
      )
      .subscribe({
        next: (resp: TrackerPositionsResponse) => {
          const content = resp?.content ?? [];
          this.positions.set(content);
          this.total.set(resp?.totalElements ?? 0);
          this.positionsLoaded.emit({ positions: content, total: resp?.totalElements ?? 0 });
          this.loading.set(false);
        },
        error: () => {
          this.positions.set([]);
          this.total.set(0);
          this.loading.set(false);
          this.#messageService.add({
            severity: 'error',
            summary: this.#transloco.translate('positionHistory.toasts.loadFailed.summary'),
            detail: this.#transloco.translate('positionHistory.toasts.loadFailed.detail'),
          });
        },
      });
  }

  /** Pull up to {@link maxPositions} sorted oldest→newest, fetch site gateways
   * in parallel, open the replay-map dialog. */
  protected openMap(): void {
    if (!this.filters?.from || this.mapLoading()) return;
    this.mapLoading.set(true);
    this.mapPositions.set([]);
    this.mapGateways.set([]);

    const siteId = this.siteId();
    if (siteId) {
      this.#siteGatewayService.getGatewaysBySite(siteId).subscribe({
        next: gws => this.mapGateways.set(gws ?? []),
        error: () => this.mapGateways.set([]),
      });
    }

    this.#positionService
      .getPositionsByDateRange(
        this.trackerId(),
        this.filters.from,
        this.filters.to ?? undefined,
        this.filters.statusFilter,
        this.filters.sourceFilter as string[] | undefined,
        0,
        this.maxPositions,
        'timestamp,asc'
      )
      .subscribe({
        next: (resp: TrackerPositionsResponse) => {
          const list = (resp?.content ?? []).filter(p => this.#isValidPosition(p));
          if (list.length === 0) {
            this.#messageService.add({
              severity: 'warn',
              summary: this.#transloco.translate('positionHistory.toasts.noPositions.summary'),
              detail: this.#transloco.translate('positionHistory.toasts.noPositions.detail'),
            });
            this.mapLoading.set(false);
            return;
          }
          if ((resp?.totalElements ?? 0) > this.maxPositions) {
            this.#messageService.add({
              severity: 'info',
              summary: this.#transloco.translate('positionHistory.toasts.capped.summary', { max: this.maxPositions }),
              detail: this.#transloco.translate('positionHistory.toasts.capped.detail', {
                max: this.maxPositions,
                total: resp.totalElements,
              }),
            });
          }
          this.mapPositions.set(list);
          this.mapDialog?.show();
          this.mapLoading.set(false);
        },
        error: () => {
          this.mapLoading.set(false);
          this.#messageService.add({
            severity: 'error',
            summary: this.#transloco.translate('positionHistory.toasts.loadFailed.summary'),
            detail: this.#transloco.translate('positionHistory.toasts.mapFailed.detail'),
          });
        },
      });
  }

  #isValidPosition(p: Position): boolean {
    return (
      p != null &&
      typeof p.latitude === 'number' &&
      typeof p.longitude === 'number' &&
      !isNaN(p.latitude) &&
      !isNaN(p.longitude) &&
      p.latitude !== 0 &&
      p.longitude !== 0
    );
  }

  protected downloadCsv(): void {
    if (!this.filters?.from) return;
    this.#positionService
      .getPositionsByDateRange(
        this.trackerId(),
        this.filters.from,
        this.filters.to ?? undefined,
        this.filters.statusFilter,
        this.filters.sourceFilter as string[] | undefined,
        0,
        100_000,
        'timestamp,asc'
      )
      .subscribe({
        next: (resp: TrackerPositionsResponse) => {
          const list = resp?.content ?? [];
          if (!list.length) {
            this.#messageService.add({
              severity: 'warn',
              summary: this.#transloco.translate('positionHistory.toasts.nothingToDownload.summary'),
              detail: this.#transloco.translate('positionHistory.toasts.nothingToDownload.detail'),
            });
            return;
          }
          const headers = [
            this.#transloco.translate('positionHistory.csvHeaders.timestamp'),
            this.#transloco.translate('positionHistory.csvHeaders.latitude'),
            this.#transloco.translate('positionHistory.csvHeaders.longitude'),
            this.#transloco.translate('positionHistory.csvHeaders.altitude'),
            this.#transloco.translate('positionHistory.csvHeaders.source'),
            this.#transloco.translate('positionHistory.csvHeaders.online'),
            this.#transloco.translate('positionHistory.csvHeaders.doppler'),
            this.#transloco.translate('positionHistory.csvHeaders.trilateration'),
          ];
          const rows = list.map(p =>
            [
              p.timestamp ? new Date(p.timestamp).toISOString() : '',
              p.latitude ?? '',
              p.longitude ?? '',
              p.altitude ?? '',
              p.source ?? '',
              p.isOnline ? 'true' : 'false',
              p.doppler ?? '',
              p.trilateration ?? '',
            ].join(',')
          );
          const csv = [headers.join(','), ...rows].join('\n');
          const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          const stamp = new Date().toISOString().replace(/[:.]/g, '-');
          a.href = url;
          a.download = `tracker-${this.deviceId() || this.trackerId()}-positions-${stamp}.csv`;
          a.style.visibility = 'hidden';
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          this.#messageService.add({
            severity: 'success',
            summary: this.#transloco.translate('positionHistory.toasts.downloaded.summary'),
            detail: this.#transloco.translate('positionHistory.toasts.downloaded.detail', { count: list.length }),
          });
        },
        error: () =>
          this.#messageService.add({
            severity: 'error',
            summary: this.#transloco.translate('positionHistory.toasts.downloadFailed.summary'),
            detail: this.#transloco.translate('positionHistory.toasts.downloadFailed.detail'),
          }),
      });
  }

  protected formatNum(n: number | null | undefined, digits: number): string {
    if (n == null || isNaN(n)) return '—';
    return n.toFixed(digits);
  }

  /** Coloured pill per source, from the shared channel palette (same hues as the maps and chips). */
  protected sourceClass(source: string): string {
    return channelPalette(source)?.pill ?? NEUTRAL_PILL_CLASS;
  }

  /** Online/offline pill — green when up, red when down. Built as a string
   * (not via [class.X] bindings) because the `dark:bg-green-500/20` arbitrary
   * value with `/` doesn't survive Angular's [class.X] parser — it interprets
   * the slash as the start of a closing tag. */
  protected onlinePillClass(isOnline: boolean | undefined): string {
    return isOnline
      ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300'
      : 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300';
  }

  /** Trilateration-quality pill — colour scale requested by ops:
   *   < 50   red         (poor)
   *   50–75  orange      (fair)
   *   75–90  light green (good)
   *   > 90   dark green  (excellent)
   * Built as a string (not [class.X] bindings) for the same `dark:bg-…/20`
   * arbitrary-value reason as the other pills. */
  protected trilaterationClass(v: number | null | undefined): string {
    if (v == null || isNaN(v)) return 'bg-gray-100 text-gray-500 dark:bg-slate-600/40 dark:text-slate-400';
    if (v < 50) return 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300';
    if (v < 75) return 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300';
    if (v <= 90) return 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300';
    return 'bg-green-700 text-white dark:bg-green-600 dark:text-white';
  }

  /** Percent label for the trilateration pill — rounded integer + `%`. */
  protected formatPct(v: number | null | undefined): string {
    if (v == null || isNaN(v)) return '—';
    return `${Math.round(v)} %`;
  }
}
